<template>
    <div class="audio-file-box box">
        <h3 class="box-title">
            Audio File
        </h3>
        <p class="box-description">
            {{ allowedExtensions }} up to {{ maxFileSize }} in size.
        </p>
        <div class="box-divider" />
        <transition name="fade" mode="out-in">
            <audio-file-form
                v-if="episodeModal.isAudioFileWaiting()"
                @submit="initializeUpload"
            />
            <audio-file-upload-status
                v-if="episodeModal.isAudioFileInitializing()"
                type="info"
                message="File upload is initialized"
            />
            <audio-file-progress
                v-if="episodeModal.isAudioFileUploading()"
                @cancel="$emit('cancel')"
            />
            <audio-file-info
                v-if="episodeModal.isAudioFileUploaded()"
                @reupload="initializeUpload"
            />
            <audio-file-upload-status
                v-if="episodeModal.isAudioFileHasError()"
                type="error"
                message="Something went wrong"
            />
        </transition>
    </div>
</template>
<script>
// External dependencies
import prettyBytes from 'pretty-bytes';
import { clone } from 'lodash';
// Internal dependencies
import AudioFileForm from './AudioFileForm';
import AudioFileInfo from './AudioFileInfo';
import AudioFileProgress from './AudioFileProgress';
import AudioFileUploadStatus from './AudioFileUploadStatus';
import episodeModal from '@/utils/episodeModal';
import { validateAudioFile } from '@/utils/form/episodeValidation';
import { audioFileAllowedExtensions, audioFileMaxSize } from '@/utils/config/episodeOptions';
import toasts from '@/mixins/toasts';

export default {
    components: {
        AudioFileForm,
        AudioFileInfo,
        AudioFileProgress,
        AudioFileUploadStatus,
    },

    mixins: [
        toasts,
    ],

    data: () => ({
        episodeModal,
        audioFileAllowedExtensions,
    }),

    computed: {
        /**
         * Gets the formatted list of allowed extensions.
         *
         * @returns {string} List of allowed extensions.
         */
        allowedExtensions() {
            const allowedExtensions = clone(audioFileAllowedExtensions);

            if (1 === allowedExtensions.length) {
                return allowedExtensions[0];
            }

            const lastItem = allowedExtensions.pop();

            return `${allowedExtensions.join(', ')} or ${lastItem}`;
        },

        /**
         * Gets the formatted max file size.
         *
         * @returns {string} Formatted max file size.
         */
        maxFileSize() {
            return prettyBytes(audioFileMaxSize);
        },
    },

    methods: {
        /**
         * Initializes file upload.
         *
         * @param  {File} file Selected file.
         */
        initializeUpload(file) {
            if (! this.validate(file)) {
                return;
            }

            this.$emit('upload', file);
        },

        /**
         * Validates given file.
         *
         * @param  {File} file File to validate.
         * @returns {boolean} Result of validation.
         */
        validate(file) {
            const error = validateAudioFile(file);

            if (error) {
                this.toastError({ message: { title: error } });
            }

            return null === error;
        },
    },
};
</script>
